import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { zip } from 'lodash';
import { BehaviorSubject, combineLatest, from, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { InfluxService } from '../influx.service';

interface Data {
  name: string;
  series: { name: Date, value: number }[];
}

interface FilterSubjects {
  value: BehaviorSubject<string>;
  click: Subject<string>;
  focus: Subject<string>;
}

@Component({
  selector: 'qm-influx-graph',
  templateUrl: './influx-graph.component.html',
  styleUrls: ['./influx-graph.component.scss']
})
export class InfluxGraphComponent implements OnChanges {
  @Input()
  public bucket: string;
  @Input()
  public measurement: string;
  @Input()
  public filterFields: string[];
  @Input()
  public filterDefaults: { [key: string]: string };

  public readonly filter$s = new Map<string, FilterSubjects>();

  // not a `BehaviorSubject` because it requires the default value synchronously
  private readonly metadata$ = new ReplaySubject<{ [key: string]: string }[]>(1);
  public readonly data$: Observable<Data[]>;

  public disabledFor = (fieldName: string): Observable<boolean> => {
    const previousFieldName = this.filterFields[this.filterFields.indexOf(fieldName) - 1];
    if (!previousFieldName) {
      return of(false);
    }
    return this.filter$s.get(previousFieldName).value.pipe(
      map(value => !value),
    );
  }

  public searchFor = (fieldName: string) => {
    const previousFieldName = this.filterFields[this.filterFields.indexOf(fieldName) - 1];
    return (text$: Observable<string>): Observable<string[]> => {
      return combineLatest([
        merge(
          text$.pipe(debounceTime(100), distinctUntilChanged()),
          this.filter$s.get(fieldName).click,
          this.filter$s.get(fieldName).focus,
        ),
        this.metadata$.pipe(
          map(metadata => metadata.filter(meta => meta[fieldName])),
        ),
        previousFieldName ? this.filter$s.get(previousFieldName).value : of<string>(null),
      ]).pipe(
        map(([text, metadata, previousValue]) => {
          if (previousValue) {
            metadata = metadata.filter(meta => meta[previousFieldName] === previousValue);
          }
          if (text) {
            metadata = metadata.filter(meta => meta[fieldName].toLowerCase().includes(text.toLowerCase()));
          }
          return Array.from(
            new Set(metadata.map(meta => meta[fieldName]))
          ).filter(x => x).sort().slice(0, 20);
        }),
      );
    };
  }

  constructor(
    private readonly influx: InfluxService,
  ) {
    this.data$ = this.metadata$.pipe(
      switchMap(() => {
        // store current content to avoid race conditions when recombining values with keys
        const filter$s = new Map(this.filter$s);

        return combineLatest(Array.from(filter$s.values()).map(x => x.value)).pipe(
          switchMap(filterValues => {
            // Map of fieldNames to current input values
            const filters = new Map(zip(Array.from(filter$s.keys()), filterValues));

            const query = {};
            for (const [key, value] of filters.entries()) {
              // only load results if all filters are filled in
              if (!value) {
                return of<Data[]>();
              }
              // _field goes in the URL path, not in the query
              if (key === '_field') {
                continue;
              }
              query[key] = value;
            }

            return from(this.influx.getFieldValues(this.bucket, this.measurement, filters.get('_field'), query)).pipe(
              map(results => [{
                name: this.measurement,
                series: results.map(result => ({
                  name: new Date(result[0]),
                  value: result[1] as number,
                })),
              }])
            );
          })
        );
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterFields || changes.filterDefaults) {
      this.filter$s.clear();
      let previousFieldName = null;
      for (const fieldName of this.filterFields) {
        this.filter$s.set(fieldName, {
          value: new BehaviorSubject(this.filterDefaults[fieldName] ?? ''),
          click: new Subject(),
          focus: new Subject(),
        });
        // reset fields to the right when a value is changed
        if (previousFieldName) {
          this.filter$s.get(previousFieldName).value.subscribe(() => {
            this.filter$s.get(fieldName).value.next(this.filterDefaults[fieldName] ?? '');
          });
        }
        previousFieldName = fieldName;
      }
    }
    if (changes.bucket || changes.measurement || changes.filterFields) {
      this.reloadMetadata();
    }
  }

  private reloadMetadata(): void {
    from(this.influx.getFields(this.bucket, this.measurement)).subscribe(this.metadata$);
  }
}
